import { buildPrescriptionDocumentData, resolveMapaPresentation, stripCrmvPrefix, type PrescriptionDocumentData, type PrescriptionSourceRecord } from "@vet/print-core";
import type { Database } from "@/integrations/supabase/types";

type MedicalPrescriptionRow = Database["public"]["Tables"]["medical_prescriptions"]["Row"];
type PetWeightSnippet = Pick<
  Database["public"]["Tables"]["pet_weights"]["Row"],
  "weight" | "measured_at" | "medical_record_id" | "deleted_at"
>;
type MedicalPrescriptionItemRow = Database["public"]["Tables"]["medical_prescription_items"]["Row"];
type MedicalPrescriptionExamItemRow =
  Database["public"]["Tables"]["medical_prescription_exam_items"]["Row"];

export type PrescriptionWithItems = MedicalPrescriptionRow & {
  issuer_address?: string | null;
  buyer_mode?: string | null;
  buyer_snapshot?: unknown;
  items: MedicalPrescriptionItemRow[];
  exam_items: MedicalPrescriptionExamItemRow[];
  vet?: { id: string; full_name: string | null; phone?: string | null; mapa_number?: string | null; address?: string | null } | null;
  clinic?: { id: string; name: string | null } | null;
  pets?: {
    id: string;
    name: string;
    birth_date?: string | null;
    species?: { name: string | null } | null;
    breed?: { name: string | null } | null;
    gender?: { name: string | null } | null;
    weights?: PetWeightSnippet[] | null;
    owners?: {
      id: string;
      name: string;
      document?: string | null;
      addresses?: Array<{
        street?: string | null;
        number?: string | null;
        neighborhood?: string | null;
        is_primary?: boolean | null;
        deleted_at?: string | null;
        city?: { name?: string | null; state?: { code?: string | null } | null } | null;
      }> | null;
    } | null;
  } | null;
  crmv?: string | null;
  mapa_number?: string | null;
  mapa_registration_snapshot?: string | null;
  mapa_prescription_identifier?: string | null;
};

const norm = (value: string | null | undefined): string => (typeof value === "string" ? value.trim() : "");

/** Adapts the Supabase row shape to the print-core source record (all layout/data rules live in print-core). */
export function toPrescriptionSourceRecord(row: PrescriptionWithItems): PrescriptionSourceRecord {
  const mapa = resolveMapaPresentation({
    controlRegime: row.control_regime,
    snapshotRegistration: row.mapa_registration_snapshot,
    snapshotIdentifier: row.mapa_prescription_identifier,
    legacyMapaNumber: row.mapa_number,
    profileMapaNumber: row.vet?.mapa_number,
  });
  const pet = row.pets;
  return {
    type: row.type ?? "standard",
    issued_at: row.issued_at ?? null,
    validity_in_days: row.validity_in_days ?? null,
    observations: row.observations ?? null,
    control_regime: row.control_regime ?? "none",
    mapa_number: row.mapa_number ?? null,
    mapa_registration_snapshot: mapa.registration,
    mapa_prescription_identifier: mapa.identifier,
    manipulated_payload: row.manipulated_payload,
    medical_record_id: row.medical_record_id ?? null,
    issuer_address: row.issuer_address ?? null,
    buyer_mode: row.buyer_mode ?? null,
    buyer_snapshot: row.buyer_snapshot ?? null,
    items: (row.items ?? []).map((item) => ({
      medication_name: item.medication_name ?? null,
      medication_concentration: item.medication_concentration ?? null,
      presentation: item.presentation ?? null,
      quantity: item.quantity ?? null,
      pharmaceutical_form: null,
      administration_route: item.administration_route ?? null,
      posology_mode: item.posology_mode ?? null,
      free_text_posology: item.free_text_posology ?? null,
      dose_amount: item.dose_amount ?? null,
      dose_unit: item.dose_unit ?? null,
      frequency_value: item.frequency_value ?? null,
      frequency_unit: item.frequency_unit ?? null,
      duration_value: item.duration_value ?? null,
      duration_unit: item.duration_unit ?? null,
    })),
    exam_items: (row.exam_items ?? []).map((item) => ({
      exam_name: item.exam_name ?? null,
      instructions: item.instructions ?? null,
    })),
    pet: pet
      ? {
          name: pet.name ?? null,
          birth_date: pet.birth_date ?? null,
          species: pet.species?.name ?? null,
          breed: pet.breed?.name ?? null,
          gender: pet.gender?.name ?? null,
          weights: (pet.weights ?? []).map((w) => ({
            weight: w.weight ?? null,
            measured_at: w.measured_at ?? null,
            medical_record_id: w.medical_record_id ?? null,
            deleted_at: w.deleted_at ?? null,
          })),
          owner: pet.owners
            ? {
                name: pet.owners.name ?? null,
                document: pet.owners.document ?? null,
                addresses: pet.owners.addresses ?? [],
              }
            : null,
        }
      : null,
    vet: {
      name: row.vet?.full_name ?? "",
      crmvForPrint: stripCrmvPrefix(norm(row.crmv)),
      phone: row.vet?.phone ?? null,
      address: row.vet?.address ?? null,
    },
    clinic: { name: row.clinic?.name ?? null },
  };
}

export const mapPrescriptionToDocumentData = (row: PrescriptionWithItems): PrescriptionDocumentData =>
  buildPrescriptionDocumentData(toPrescriptionSourceRecord(row));
